import { useState } from 'react';
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import { Flag } from '@interstate/components/Flag';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { IconButton } from '@interstate/components/IconButton';
import { Typography } from '@interstate/components/Typography';
import { EyeIcon, EyeSlashIcon } from '@interstate/components/Icons';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import {
    SOURCE_TYPE_DEALER,
    ALLOWANCE_LABEL,
    ALLOWANCE_SOURCE_LABEL_MANAGER,
    ALLOWANCE_SOURCE_LABEL_SALESPERSON
} from '@makemydeal/dr-dash-store/constants';

import { usePaymentCall } from '../../../utils/usePaymentCall';
import { useFieldUpdatingIndicator } from '../../../hooks/useFieldUpdatingIndicator';
import { dealLockHooks } from '../../../hooks';
import { FieldUpdatingSpinner } from '../../../common/FieldUpdatingSpinner';
import { AppraisalStatusPill } from './AppraisalStatusPill';
import TradeItemHeader from './TradeItemHeader';
import { useTradeItemSelectors } from './useTradeItemSelectors';
import { AllowanceLabelRow } from './TradeItem.style';
import { useTrackedSelector } from '../../../telemetry/useTrackedSelector';

type TradeItemProps = {
    year?: number | string | null;
    make?: string | null;
    model?: string | null;
    originalIndex: number;
    isDeleted?: boolean;
    source?: string | null;
};

const TradeItem = ({ year, make, model, originalIndex, isDeleted = false, source }: TradeItemProps) => {
    const [showAcv, setShowAcv] = useState(false);
    const toggleAcv = () => setShowAcv((prev) => !prev);
    const isAllowanceSourceIndicatorEnabled = useTrackedSelector(featureToggleSelectors.isAllowanceSourceIndicatorEnabled, {
        component: 'TradeItem',
        field: 'allowanceSourceIndicatorEnabled',
        instanceKey: originalIndex
    });
    // An appraisal refresh can overwrite the ACV, and that update does not come from this field.
    const isRefreshingAppraisals = useTrackedSelector(vAutoSelectors.getIsRefreshingAppraisals, {
        component: 'TradeItem',
        field: 'isRefreshingAppraisals',
        instanceKey: originalIndex
    });
    const isLocked = dealLockHooks.useIsDealLocked();
    const isTradeAcvOverrideEnabled = useTrackedSelector(dealerSelectors.isTradeAcvOverrideEnabled, {
        component: 'TradeItem',
        field: 'tradeAcvOverrideEnabled',
        instanceKey: originalIndex
    });
    const vin = useTrackedSelector(tradeInsSelectors.getVin(originalIndex), {
        component: 'TradeItem',
        field: 'vin',
        instanceKey: originalIndex
    });
    const vAutoAppraisalStatus = useTrackedSelector(vAutoSelectors.getCurrentAppraisalByVin(vin), {
        component: 'TradeItem',
        field: 'currentAppraisalByVin',
        instanceKey: originalIndex
    })?.status;
    const isAcvDisabled = isDeleted || isLocked || (!!vAutoAppraisalStatus && !isTradeAcvOverrideEnabled);

    const { allowanceSelector, payoffSelector, acvSelector, allowanceAction, payoffAction, acvAction } = useTradeItemSelectors(
        originalIndex,
        isAllowanceSourceIndicatorEnabled
    );

    // Use payment call hooks for each field
    const {
        value: allowance,
        handleKeyDown: allowanceKeyDown,
        handleChange: changeAllowance,
        handleBlur: allowanceBlur
    } = usePaymentCall(allowanceSelector, allowanceAction, {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const {
        value: payoff,
        handleKeyDown: payoffKeyDown,
        handleChange: changePayoff,
        handleBlur: payoffBlur
    } = usePaymentCall(payoffSelector, payoffAction, {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    const { actionCreator: trackedAcvAction, isUpdating: isAcvUpdating } = useFieldUpdatingIndicator(acvAction);
    const {
        value: acv,
        handleKeyDown: acvKeyDown,
        handleChange: changeAcv,
        handleBlur: acvBlur
    } = usePaymentCall(acvSelector, trackedAcvAction, {
        formatValueToString: formatNumberAndCents,
        ignoreTextValue: () => false
    });

    // Mask ACV value when hidden - replace all characters with bullets
    const obfuscatedAcv = acv.replace(/./g, '•');

    const inputSx = { '& input': { caretColor: 'auto' } };

    // The ACV field is identical in both layouts below, so it is built once here. Its trailing slot
    // shows a spinner while an ACV change is still recalculating, and keeps the masking overlay in the
    // hidden state so the value cannot flash into view mid-update.
    const acvTrailingControl =
        isAcvUpdating || isRefreshingAppraisals ? (
            <FieldUpdatingSpinner testId={`trade-acv-updating-${originalIndex}`} />
        ) : (
            <IconButton size="sm" buttonStyle="tertiary" onClick={toggleAcv} data-testid={`trade-acv-toggle-${originalIndex}`}>
                {showAcv ? <EyeSlashIcon /> : <EyeIcon />}
            </IconButton>
        );

    const acvInput = (
        <NumericInput
            inputPrefix={'$'}
            autoInsertCommas={showAcv}
            allowDecimal
            decimalMaxLength={2}
            decimalMinLength={0}
            label="ACV"
            name={`trade-acv-${originalIndex}`}
            data-testid={`trade-acv-${originalIndex}`}
            value={acv}
            onChange={changeAcv}
            onBlur={acvBlur}
            onKeyDown={acvKeyDown}
            size="sm"
            disabled={isAcvDisabled}
            sx={{ position: 'relative', '& svg': { color: 'inherit!important' }, ...inputSx }}
            inputSuffix={
                showAcv ? (
                    <Box position="relative" left={2}>
                        {acvTrailingControl}
                    </Box>
                ) : (
                    <Box
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 2,
                            bottom: 2,
                            left: 22,
                            right: 2,
                            background: isAcvDisabled ? theme.tokens.ComponentBtnDisabledBg : theme.tokens.BaseColorWhite,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        })}
                    >
                        <Typography variant="body-md">{obfuscatedAcv}</Typography>
                        {acvTrailingControl}
                    </Box>
                )
            }
        />
    );

    return (
        <Box mb={1} data-testid={`trade-${originalIndex}`}>
            <TradeItemHeader originalIndex={originalIndex} year={year} make={make} model={model} isDeleted={isDeleted} />

            {isAllowanceSourceIndicatorEnabled ? (
                <>
                    <Grid container gap="4px" display="flex" flexWrap="nowrap" mb={1}>
                        <Grid xs={12}>
                            {source && (
                                <AllowanceLabelRow>
                                    <Typography variant="label-sm">{ALLOWANCE_LABEL}</Typography>
                                    <Flag variant="light">
                                        {source === SOURCE_TYPE_DEALER
                                            ? ALLOWANCE_SOURCE_LABEL_MANAGER
                                            : ALLOWANCE_SOURCE_LABEL_SALESPERSON}
                                    </Flag>
                                </AllowanceLabelRow>
                            )}
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                label={source ? '' : ALLOWANCE_LABEL}
                                name={`trade-allowance-${originalIndex}`}
                                data-testid={`trade-allowance-${originalIndex}`}
                                value={allowance}
                                onChange={changeAllowance}
                                onBlur={allowanceBlur}
                                onKeyDown={allowanceKeyDown}
                                size="sm"
                                disabled={isDeleted}
                                sx={inputSx}
                            />
                        </Grid>
                    </Grid>
                    <Grid container gap="4px" display="flex" flexWrap="nowrap" mb={1}>
                        <Grid xs={6}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                label="Payoff"
                                name={`trade-payoff-${originalIndex}`}
                                data-testid={`trade-payoff-${originalIndex}`}
                                value={payoff}
                                onChange={changePayoff}
                                onBlur={payoffBlur}
                                onKeyDown={payoffKeyDown}
                                size="sm"
                                disabled={isDeleted}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid xs={6}>{acvInput}</Grid>
                    </Grid>
                </>
            ) : (
                <>
                    <Grid container gap="4px" display="flex" flexWrap="nowrap" mb={1}>
                        <Grid xs={6}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                label={ALLOWANCE_LABEL}
                                name={`trade-allowance-${originalIndex}`}
                                data-testid={`trade-allowance-${originalIndex}`}
                                value={allowance}
                                onChange={changeAllowance}
                                onBlur={allowanceBlur}
                                onKeyDown={allowanceKeyDown}
                                size="sm"
                                disabled={isDeleted}
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid xs={6}>
                            <NumericInput
                                inputPrefix={'$'}
                                autoInsertCommas
                                allowDecimal
                                decimalMaxLength={2}
                                decimalMinLength={0}
                                label="Payoff"
                                name={`trade-payoff-${originalIndex}`}
                                data-testid={`trade-payoff-${originalIndex}`}
                                value={payoff}
                                onChange={changePayoff}
                                onBlur={payoffBlur}
                                onKeyDown={payoffKeyDown}
                                size="sm"
                                disabled={isDeleted}
                                sx={inputSx}
                            />
                        </Grid>
                    </Grid>
                    {acvInput}
                </>
            )}

            {/* Placed outside the layout branches so both allowance layouts get the pill. */}
            <AppraisalStatusPill tradeInIndex={originalIndex} />
        </Box>
    );
};

export default TradeItem;
